import { Injectable } from '@angular/core';

export interface PhotoViewerData {
    photos: string[];           // Full URLs for display
    downloadPhotos: string[];   // Proxy URLs for download
    startIndex: number;
    title?: string;             // Optional title for the viewer tab
}

@Injectable({ providedIn: 'root' })
export class PhotoViewerService {

    private storageKey = 'photo_viewer_data';

    setPhotos(data: PhotoViewerData): void {
        localStorage.setItem(this.storageKey, JSON.stringify(data));
    }

    getPhotos(): PhotoViewerData {
        const raw = localStorage.getItem(this.storageKey);
        return raw
            ? JSON.parse(raw)
            : { photos: [], downloadPhotos: [], startIndex: 0, title: '' };
    }

    clear(): void {
        localStorage.removeItem(this.storageKey);
    }

    /**
     * Helper: Build viewer data from raw file paths
     * Works with any file structure from any module
     */
    buildViewerData(
        files: any[],
        filePathKey: string,          // e.g., 'filePath', 'photoPath', 'imagePath'
        imageBaseUrl: string,         // e.g., 'https://localhost:44391/uploads/'
        clickedFilePath: string,
        options?: {
            title?: string;
            imageExtensions?: string[];
        }
    ): PhotoViewerData {

        const extensions = options?.imageExtensions
            || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

        // Filter only image files
        const imageFiles = files.filter(f => {
            const path = f[filePathKey] || '';
            const ext = path.split('.').pop()?.toLowerCase() || '';
            return extensions.includes(ext);
        });

        // Build full URLs
        const fullUrls = imageFiles.map(f => {
            const clean = (f[filePathKey] || '').replace(/\\/g, '/');
            return clean.startsWith('http') ? clean : imageBaseUrl + clean;
        });

        // Build proxy download URLs
        const downloadUrls = imageFiles.map(f => {
            const clean = (f[filePathKey] || '').replace(/\\/g, '/');
            if (clean.startsWith('http')) {
                // Extract path after domain
                try {
                    const url = new URL(clean);
                    return url.pathname;
                } catch {
                    return clean;
                }
            }
            return '/uploads/' + clean;
        });

        // Find clicked index
        const cleanClicked = clickedFilePath.replace(/\\/g, '/');
        const clickedUrl = cleanClicked.startsWith('http')
            ? cleanClicked
            : imageBaseUrl + cleanClicked;
        const index = fullUrls.indexOf(clickedUrl);

        return {
            photos: fullUrls,
            downloadPhotos: downloadUrls,
            startIndex: index >= 0 ? index : 0,
            title: options?.title || 'Photo Viewer'
        };
    }
}